import * as Clipboard from "expo-clipboard";
// ====== React imports ======
import { useMemo, useState } from "react";
// ====== External Libraries ======
import { IconButton, Menu } from "react-native-paper";

import Box from "@memorang/ui/src/components/Box";
import { useToast } from "@memorang/ui/src/components/Toast";
import TooltipWrapper from "@memorang/ui/src/components/TooltipWrapper";
// ====== Local components ======
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import { TTSContainer } from "../TTS";
import { FeedbackDialog, type FeedbackParams } from "../feedback";
import type { HandleTracking } from "../types";
import { useAppTheme } from "../useAppTheme";
import {
	events,
	ENDPOINT_RESEND_EMAIL,
	feedbackActionIcons,
	summaryActionIcons,
} from "./constants";

// const emailInstructionMessage = (email?: string) => {
// 	const isEduEmail = email?.endsWith(".edu");
// 	return `If you do not receive an email, check your junk, spam, and recently deleted folders${isEduEmail ? ", or contact your IT administrator" : ""}`;
// };
type Props = {
	text?: string;
	searchQuery?: string;
	showEmail?: boolean;
	streaming?: boolean;
	handleTracking: HandleTracking;
	feedbackDialogParams?: FeedbackParams;
	align?:
		| "flex-start"
		| "flex-end"
		| "center"
		| "space-between"
		| "space-around"
		| "space-evenly"
		| undefined;
};

const SummaryActions = ({
	text,
	handleTracking,
	feedbackDialogParams,
	align = "flex-start",
	searchQuery,
	showEmail,
	streaming,
}: Props) => {
	const { t } = useTranslation();
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const [isEmailSent, setIsEmailSent] = useState(false);
	const [inProgress, setInProgress] = useState<Record<string, boolean>>({});
	const [isMenuVisible, setIsMenuVisible] = useState(false);
	const [feedbackType, setFeedbackType] = useState<
		"positive" | "negative" | "reportIssue"
	>();
	const { showToast } = useToast();
	const theme = useAppTheme();
	const toggleShowFeedbackDialog = (): void => {
		setShowFeedbackDialog((prev) => !prev);
	};

	const handleSendEmail = async () => {
		await fetch(ENDPOINT_RESEND_EMAIL, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				examName: feedbackDialogParams?.context,
				markdown: text,
				toEmail: feedbackDialogParams?.email,
				companyName: feedbackDialogParams?.companyName,
			}),
		});
		handleTracking(events.emailSent);
	};

	const handleActionIconPress = async (type: string) => {
		switch (type) {
			case "copy": {
				const textToCopy = searchQuery
					? `# Question:\n${searchQuery}\n\n# Answer:\n${text}`
					: text!;
				await Clipboard.setStringAsync(textToCopy);
				handleTracking(events.sessionReportSummaryCopyClicked, {
					query: searchQuery!,
					summary: text!,
				});
				showToast({
					message: t("report.sessionAnalysis.menuActions.copiedAnalysis"),
					duration: 3000,
				});
				break;
			}
			case "feedback-positive":
			case "feedback-negative": {
				setFeedbackType(type === "feedback-positive" ? "positive" : "negative");
				handleTracking(
					type === "feedback-positive"
						? events.aiFeedbackPositive
						: events.aiFeedbackNegative,
				);
				toggleShowFeedbackDialog();
				break;
			}
			case "report-issue": {
				setFeedbackType("reportIssue");
				handleTracking(events.aiFeedbackReport);
				toggleShowFeedbackDialog();
				break;
			}
			case "email": {
				try {
					setInProgress({ ...inProgress, email: true });
					handleTracking(events.emailTriggered);
					await handleSendEmail();
					setIsEmailSent(true);
					showToast({
						message: t("report.sessionAnalysis.menuActions.emailSent", {
							email: feedbackDialogParams?.email,
							emailInstructionMessage: t(
								"report.sessionAnalysis.menuActions.emailInstructionMessage",
							),
						}),
						duration: 3000,
					});
				} catch (error) {
					showToast({
						message: t("report.sessionAnalysis.menuActions.emailFailed", {
							email: feedbackDialogParams?.email,
						}),
						duration: 3000,
					});
					console.error(error);
				} finally {
					setInProgress({ ...inProgress, email: false });
				}
				break;
			}
			default:
				break;
		}
	};

	const handleOpenMenu = () => {
		setIsMenuVisible(true);
	};

	const handleCloseMenu = () => {
		setIsMenuVisible(false);
	};

	const actionItems = useMemo(() => {
		if (Platform.OS === "web") {
			return [
				...summaryActionIcons(showEmail, isEmailSent),
				...feedbackActionIcons,
			];
		}
		return summaryActionIcons(showEmail, isEmailSent);
	}, [showEmail, isEmailSent]);

	const handlePostFeedback = (feedback: string, email: string) => {
		handleTracking(events.feedbackSubmitted, {
			type: feedbackType!,
			feedback,
			email,
		});
	};

	return (
		<Box flexDirection="row" justifyContent={align} alignItems="center">
			{actionItems.map(({ icon, type, tooltip, disabled }) => (
				<TooltipWrapper
					key={icon}
					title={t(tooltip) || ""}
					enterTouchDelay={500}
				>
					<IconButton
						icon={icon}
						size={18}
						iconColor={theme.colors.onPrimaryContainer}
						loading={inProgress[type]}
						onPress={() => handleActionIconPress(type)}
						style={{
							margin: 0,
						}}
						disabled={disabled || inProgress[type] || streaming}
					/>
				</TooltipWrapper>
			))}
			<TTSContainer
				explanation={text}
				size={16}
				itemId={`${feedbackDialogParams?.sessionId}`}
				mode={undefined}
				iconColor={theme.colors.onPrimaryContainer}
			/>
			{Platform.OS !== "web" && (
				<Menu
					visible={isMenuVisible}
					onDismiss={handleCloseMenu}
					anchorPosition="bottom"
					anchor={
						<IconButton
							icon="dots-vertical"
							size={18}
							iconColor={theme.colors.onPrimaryContainer}
							onPress={handleOpenMenu}
							style={{
								margin: 0,
							}}
						/>
					}
				>
					{feedbackActionIcons.map(({ icon, type, tooltip }) => (
						<Menu.Item
							key={icon}
							leadingIcon={icon}
							title={t(tooltip)}
							onPress={() => {
								handleActionIconPress(type);
								handleCloseMenu();
							}}
						/>
					))}
				</Menu>
			)}
			{showFeedbackDialog && feedbackDialogParams && (
				<FeedbackDialog
					visible={showFeedbackDialog}
					handlePostFeedback={handlePostFeedback}
					handleTracking={handleTracking}
					onDismiss={toggleShowFeedbackDialog}
					feedbackDialogParams={{
						...feedbackDialogParams,
						type:
							feedbackType === "reportIssue"
								? "Content Flagged"
								: feedbackDialogParams.type,
					}}
					feedbackPlaceholder={t(
						"report.sessionAnalysis.feedbackDialog.placeholder",
					)}
					icon={
						feedbackType === "reportIssue"
							? "flag-outline"
							: "comment-quote-outline"
					}
					title={t(
						feedbackType === "reportIssue"
							? "dialog.report.title"
							: "report.sessionAnalysis.feedbackDialog.title",
					)}
					subtitle={t(
						feedbackType === "reportIssue"
							? "dialog.report.body"
							: "report.sessionAnalysis.feedbackDialog.body",
					)}
					cta={
						feedbackType === "reportIssue" ? t("dialog.report.cta") : undefined
					}
				/>
			)}
		</Box>
	);
};

export { SummaryActions };
